import { useCallback, useEffect, useState } from 'react';
import { Ban, Loader2, RefreshCw, ShieldCheck, Tags, Trash2 } from 'lucide-react';
import { messagesApi, tagsApi } from '../../lib/api';

// Riga della blocklist restituita da GET /messages/blocklist.
interface BlockedSender {
  id: string;
  sender_email: string;
  sender_domain: string | null;
  reason: string | null;
  created_at: string;
}

// TAB Blocklist: elenco dei mittenti bloccati (blocco SOLO logico, nessuna
// azione su Merak/IMAP). Consente di sbloccare un mittente e di ri-scansionare
// lo storico per marcare come spam le mail dei mittenti in blocklist.
export function BlocklistTab() {
  const [items, setItems] = useState<BlockedSender[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rescanning, setRescanning] = useState(false);
  const [rescanMsg, setRescanMsg] = useState<string | null>(null);

  // Carica l'elenco dei mittenti bloccati.
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = (await messagesApi.blocklist()) as { items: BlockedSender[] };
      setItems(res.items ?? []);
    } catch {
      setError('Impossibile caricare la blocklist.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Sblocca un mittente (rimuove la riga). Le mail gia' marcate spam restano
  // tali finche' non vengono ri-taggate manualmente.
  const unblock = async (id: string) => {
    if (!window.confirm('Sbloccare questo mittente? Le mail gia’ segnate come spam restano invariate.'))
      return;
    setBusyId(id);
    try {
      await messagesApi.unblock(id);
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch {
      setError('Errore durante lo sblocco del mittente.');
    } finally {
      setBusyId(null);
    }
  };

  // Ri-scansiona lo storico: marca is_spam + tag 'spam' su tutte le mail il cui
  // mittente e' in blocklist. Nessun costo AI.
  const rescan = async () => {
    setRescanning(true);
    setRescanMsg(null);
    try {
      const res = (await messagesApi.rescanSpam()) as { ok: boolean; flagged: number };
      setRescanMsg(`Ri-scansione completata: ${res.flagged ?? 0} mail marcate come spam.`);
    } catch {
      setRescanMsg('Errore durante la ri-scansione dello storico.');
    } finally {
      setRescanning(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Intestazione + azioni */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-zinc-100">
            <Ban size={14} className="text-red-400" /> Mittenti bloccati
          </h3>
          <p className="mt-1 text-xs text-zinc-500">
            Blocco solo logico: le mail dei mittenti bloccati vengono evidenziate come spam,
            senza alcuna azione sulla casella di posta.
          </p>
        </div>
        <button
          onClick={rescan}
          disabled={rescanning || items.length === 0}
          className="flex shrink-0 items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-800/60 px-2.5 py-1.5 text-xs font-medium text-zinc-200 transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
          title="Marca come spam lo storico dei mittenti in blocklist"
        >
          {rescanning ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          Ri-scansiona storico
        </button>
      </div>

      {rescanMsg && (
        <div className="rounded-md border border-zinc-700 bg-zinc-800/40 px-3 py-2 text-xs text-zinc-300">
          {rescanMsg}
        </div>
      )}
      {error && (
        <div className="rounded-md border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
          {error}
        </div>
      )}

      {/* Elenco */}
      {loading ? (
        <div className="flex items-center gap-2 py-8 text-xs text-zinc-500">
          <Loader2 size={14} className="animate-spin" /> Caricamento in corso...
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed border-zinc-800 py-10 text-center text-xs text-zinc-500">
          Nessun mittente bloccato. Usa &laquo;Blocca mittente&raquo; dalla lista mail per aggiungerne.
        </div>
      ) : (
        <ul className="divide-y divide-zinc-800 overflow-hidden rounded-md border border-zinc-800">
          {items.map((it) => (
            <li key={it.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <div className="truncate text-xs font-medium text-zinc-200">{it.sender_email}</div>
                <div className="text-[11px] text-zinc-500">
                  {it.sender_domain ? `dominio ${it.sender_domain} · ` : ''}
                  bloccato il {new Date(it.created_at).toLocaleDateString('it-IT')}
                </div>
              </div>
              <button
                onClick={() => unblock(it.id)}
                disabled={busyId === it.id}
                className="flex shrink-0 items-center gap-1 rounded px-2 py-1 text-[11px] font-medium text-zinc-400 transition hover:bg-red-950/40 hover:text-red-300 disabled:opacity-50"
                title="Sblocca questo mittente"
              >
                {busyId === it.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                Sblocca
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Mittenti/domini SEMPRE attendibili (allowlist) */}
      <AllowlistSection />

      {/* Regole tag ereditarie */}
      <TagRulesSection />
    </div>
  );
}

// Riga allowlist restituita da GET /messages/allowlist.
interface AllowedSender {
  id: string;
  sender_email: string;
  sender_domain: string | null;
  created_at: string;
}

// Sezione "Attendibili": mittenti/domini che non finiranno MAI in spam.
// Si popolano togliendo una mail dallo spam con ambito Mittente/Dominio.
function AllowlistSection() {
  const [items, setItems] = useState<AllowedSender[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = (await messagesApi.allowlist()) as { items: AllowedSender[] };
      setItems(res.items ?? []);
    } catch {
      /* silenzioso */
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (id: string) => {
    if (!window.confirm('Rimuovere questa regola? Il mittente potra\u2019 di nuovo finire in spam.')) return;
    setBusyId(id);
    try {
      await messagesApi.removeAllowlist(id);
      setItems((prev) => prev.filter((it) => it.id !== id));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="pt-2">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-zinc-100">
        <ShieldCheck size={14} className="text-emerald-400" /> Mittenti attendibili
      </h3>
      <p className="mt-1 text-xs text-zinc-500">
        Questi mittenti/domini non verranno mai marcati come spam. Si aggiungono togliendo una
        mail dallo spam con ambito &laquo;Mittente&raquo; o &laquo;Dominio&raquo;.
      </p>
      {loading ? (
        <div className="flex items-center gap-2 py-4 text-xs text-zinc-500">
          <Loader2 size={14} className="animate-spin" /> Caricamento…
        </div>
      ) : items.length === 0 ? (
        <div className="mt-2 rounded-md border border-dashed border-zinc-800 py-6 text-center text-xs text-zinc-500">
          Nessun mittente attendibile ancora.
        </div>
      ) : (
        <ul className="mt-2 divide-y divide-zinc-800 overflow-hidden rounded-md border border-zinc-800">
          {items.map((it) => (
            <li key={it.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <div className="truncate text-xs font-medium text-zinc-200">
                  {it.sender_domain ? `@${it.sender_domain} (tutto il dominio)` : it.sender_email}
                </div>
                <div className="text-[11px] text-zinc-500">
                  attendibile dal {new Date(it.created_at).toLocaleDateString('it-IT')}
                </div>
              </div>
              <button
                onClick={() => remove(it.id)}
                disabled={busyId === it.id}
                className="flex shrink-0 items-center gap-1 rounded px-2 py-1 text-[11px] font-medium text-zinc-400 transition hover:bg-red-950/40 hover:text-red-300 disabled:opacity-50"
                title="Rimuovi dalla lista attendibili"
              >
                {busyId === it.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                Rimuovi
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Riga regola tag restituita da GET /tags/rules.
interface TagRule {
  id: string;
  kind: 'sender' | 'domain' | 'content';
  pattern: string;
  created_at: string;
  tag: { id: string; name: string; color: string } | null;
}

const RULE_KIND_LABEL: Record<TagRule['kind'], string> = {
  sender: 'mittente',
  domain: 'dominio',
  content: 'contenuto',
};

// Sezione "Regole tag": regole ereditarie mittente/dominio/contenuto -> tag.
// Si creano dalla lista mail (pulsante Tag della riga, scegliendo l'ambito).
function TagRulesSection() {
  const [items, setItems] = useState<TagRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = (await tagsApi.rules()) as { items: TagRule[] };
      setItems(res.items ?? []);
    } catch {
      /* silenzioso */
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (id: string) => {
    const removeTags = window.confirm(
      'Rimuovere anche il tag dalle mail gia\u2019 taggate da questa regola?\nOK = si\u2019, Annulla = mantieni i tag esistenti.',
    );
    setBusyId(id);
    try {
      await tagsApi.removeRule(id, removeTags);
      setItems((prev) => prev.filter((it) => it.id !== id));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="pt-2">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-zinc-100">
        <Tags size={14} className="text-accent-400" /> Regole tag ereditarie
      </h3>
      <p className="mt-1 text-xs text-zinc-500">
        Tutte le mail (presenti e future) di un mittente/dominio o con un certo contenuto
        ereditano automaticamente il tag. Si creano dalla lista mail: pulsante
        &laquo;Tag&raquo; sulla riga, scegliendo l&rsquo;ambito.
      </p>
      {loading ? (
        <div className="flex items-center gap-2 py-4 text-xs text-zinc-500">
          <Loader2 size={14} className="animate-spin" /> Caricamento…
        </div>
      ) : items.length === 0 ? (
        <div className="mt-2 rounded-md border border-dashed border-zinc-800 py-6 text-center text-xs text-zinc-500">
          Nessuna regola tag ancora.
        </div>
      ) : (
        <ul className="mt-2 divide-y divide-zinc-800 overflow-hidden rounded-md border border-zinc-800">
          {items.map((it) => (
            <li key={it.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-xs font-medium text-zinc-200">
                  {it.tag && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                      style={{ background: (it.tag.color ?? '#6b7280') + '33', color: it.tag.color ?? '#d4d4d8' }}
                    >
                      {it.tag.name}
                    </span>
                  )}
                  <span className="truncate">
                    {RULE_KIND_LABEL[it.kind]}: {it.kind === 'domain' ? '@' : ''}{it.pattern}
                  </span>
                </div>
                <div className="text-[11px] text-zinc-500">
                  creata il {new Date(it.created_at).toLocaleDateString('it-IT')}
                </div>
              </div>
              <button
                onClick={() => remove(it.id)}
                disabled={busyId === it.id}
                className="flex shrink-0 items-center gap-1 rounded px-2 py-1 text-[11px] font-medium text-zinc-400 transition hover:bg-red-950/40 hover:text-red-300 disabled:opacity-50"
                title="Elimina questa regola"
              >
                {busyId === it.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                Elimina
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
